// Central API fetch utility for Nova Web

import { refreshAccessToken } from "./auth";
import {
	clearTokens,
	getAccessToken,
	getRefreshToken,
	isAccessTokenExpired,
} from "./tokenStorage";

const BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";

// Track if we're currently refreshing to avoid multiple refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Attempt to refresh the access token if we have a refresh token
 * Returns null if refresh fails or no refresh token is available
 */
async function attemptTokenRefresh(): Promise<string | null> {
	// Don't try to refresh if we're already refreshing
	if (isRefreshing && refreshPromise) {
		return refreshPromise;
	}

	// Don't try to refresh if we don't have a refresh token
	const refreshToken = getRefreshToken();
	if (!refreshToken) {
		return null;
	}

	isRefreshing = true;
	refreshPromise = refreshAccessToken()
		.then((tokens) => {
			isRefreshing = false;
			refreshPromise = null;
			return tokens?.accessToken || null;
		})
		.catch((error) => {
			isRefreshing = false;
			refreshPromise = null;

			// Extract error message for better logging
			const errorMessage =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message ||
				"Token refresh failed";

			console.warn(
				"[apiFetch] Token refresh failed:",
				errorMessage,
				error?.response?.status === 401
					? "(Refresh token invalid/expired - user needs to log in again)"
					: "",
			);

			// Clear tokens if refresh fails (refresh token is invalid/expired)
			clearTokens();
			return null;
		});

	return refreshPromise;
}

export async function apiFetch<T>(
	endpoint: string,
	options: RequestInit = {},
): Promise<T> {
	let accessToken = getAccessToken();

	// Proactively refresh token if it's expired (before making the request)
	// Skip for refresh endpoint and public endpoints
	if (
		accessToken &&
		isAccessTokenExpired() &&
		!endpoint.includes("/auth/refresh") &&
		!endpoint.includes("/auth/login") &&
		!endpoint.includes("/auth/signup")
	) {
		console.log("[apiFetch] Access token expired, refreshing proactively...");
		const newAccessToken = await attemptTokenRefresh();
		if (newAccessToken) {
			accessToken = newAccessToken;
		}
	}

	let res: Response;
	try {
		// Don't set Content-Type for FormData - browser will set it with boundary
		const isFormData = options.body instanceof FormData;
		const headers: Record<string, string> = {
			...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
			...((options.headers as Record<string, string>) || {}),
		};

		if (!isFormData) {
			headers["Content-Type"] = "application/json";
		}

		res = await fetch(`${BASE_URL}${endpoint}`, {
			...options,
			credentials: "include",
			headers,
		});

		// If we got a 401 and we have an access token, try to refresh and retry once
		// Skip refresh for the refresh endpoint itself to avoid infinite loops
		if (
			res.status === 401 &&
			accessToken &&
			!endpoint.includes("/auth/refresh")
		) {
			const newAccessToken = await attemptTokenRefresh();
			if (newAccessToken) {
				// Retry the request with the new token
				headers.Authorization = `Bearer ${newAccessToken}`;
				res = await fetch(`${BASE_URL}${endpoint}`, {
					...options,
					credentials: "include",
					headers,
				});
			}
		}
	} catch (error) {
		// Network error - server unreachable, CORS issue, or connection failed
		const errorMessage =
			error instanceof TypeError && error.message === "Failed to fetch"
				? `Unable to connect to the server. Please check that the API is running at ${BASE_URL} and try again.`
				: error instanceof Error
					? error.message
					: "Network error. Please check your connection and try again.";
		const networkError = new Error(errorMessage);
		(networkError as any).status = 0;
		(networkError as any).code = "NETWORK_ERROR";
		(networkError as any).isNetworkError = true;
		throw networkError;
	}

	if (!res.ok) {
		let errorMessage = "Something went wrong. Please try again.";
		let errorCode = "UNKNOWN_ERROR";
		let redirectTo: string | undefined;
		try {
			const errorData = await res.json();
			if (typeof errorData === "string") {
				errorMessage = errorData;
			} else if (errorData && typeof errorData === "object") {
				// Handle nested error format: { message: { error: { code, message } } }
				if (errorData.message?.error?.message) {
					errorMessage = errorData.message.error.message;
					errorCode = errorData.message.error.code || errorCode;
					// Check for redirectTo in details
					if (errorData.message.error.details?.redirectTo) {
						redirectTo = errorData.message.error.details.redirectTo;
					}
				}
				// Handle Nova error format: { error: { code, message, details } }
				else if (errorData.error?.message) {
					errorMessage = errorData.error.message;
					errorCode = errorData.error.code || errorCode;
					// Check for redirectTo in details
					if (errorData.error.details?.redirectTo) {
						redirectTo = errorData.error.details.redirectTo;
					}
				}
				// Handle simple message format
				else if (typeof errorData.message === "string" && errorData.message) {
					errorMessage = errorData.message;
				}
				// Handle error string format
				else if (typeof errorData.error === "string" && errorData.error) {
					errorMessage = errorData.error;
				}
				// Handle message array format
				else if (Array.isArray(errorData.message)) {
					errorMessage = errorData.message.join(", ");
				}
			}
		} catch {
			try {
				const text = await res.text();
				if (text && typeof text === "string") {
					errorMessage = text;
				}
			} catch {}
		}
		const error = new Error(errorMessage);
		(error as any).status = res.status;
		(error as any).code = errorCode;
		if (redirectTo) {
			(error as any).redirectTo = redirectTo;
		}
		throw error;
	}

	// Handle 204 No Content responses (common for DELETE requests)
	if (res.status === 204) {
		return undefined as T;
	}

	// Check if response has content before trying to parse JSON
	const contentType = res.headers.get("content-type");
	if (!contentType || !contentType.includes("application/json")) {
		// If not JSON, try to get text or return undefined
		try {
			const text = await res.text();
			return (text || undefined) as T;
		} catch {
			return undefined as T;
		}
	}

	const parsed = (await res.json()) as unknown;

	if (
		parsed &&
		typeof parsed === "object" &&
		!Array.isArray(parsed) &&
		"data" in parsed &&
		(parsed as { data?: unknown }).data !== undefined
	) {
		return (parsed as { data: T }).data;
	}

	return parsed as T;
}
